import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tag } from 'antd';
import {
  RocketOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ReloadOutlined,
  SearchOutlined,
  RetweetOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeLisbonShort } from '@crm/utils/lisbonDateTime';
import '@crm/components/CallCenter/CallCenterDashboard.css';
import { RecuperacaoMarcacao } from '../api/cccApi';
import { CCC_HOME_ACTIONS, CccHomeActionVariant } from '../config/cccHomeNav';
import { getMarcacaoRowSummary, getWorkspaceSubstage } from '../utils/recuperacaoMarcacaoDisplay';
import { subsectionLabel } from '../config/recuperacaoWorkspace';
import '../styles/cccHome.css';

type NewsItem = {
  id: number;
  title: string;
  content: string;
  category: string;
  priority: string;
  author_name?: string;
  created_at: string;
  is_pinned?: boolean;
};

const ACTION_ICONS: Record<CccHomeActionVariant, React.ReactNode> = {
  recuperacao: <ReloadOutlined className="cc-action-icon" />,
  calendario: <CalendarOutlined className="cc-action-icon" />,
  prospeccao: <SearchOutlined className="cc-action-icon" />,
  retencao: <RetweetOutlined className="cc-action-icon" />,
  resolucao: <CheckCircleOutlined className="cc-action-icon" />,
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const mainPageContainerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const newsRef = useRef<HTMLDivElement>(null);

  const [currentSection, setCurrentSection] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [recentMine, setRecentMine] = useState<RecuperacaoMarcacao[]>([]);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loadingNews, setLoadingNews] = useState(true);

  const sections = [
    { name: 'Início', icon: <RocketOutlined /> },
    { name: 'Notícias', icon: <FileTextOutlined /> },
  ];

  const fetchNews = useCallback(async () => {
    setLoadingNews(true);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
      const res = await fetch('/api/callcenter/news', {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        setNewsItems([]);
        return;
      }
      const data = await res.json();
      const news = Array.isArray(data.news) ? data.news : [];
      news.sort((a: NewsItem, b: NewsItem) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setNewsItems(news.slice(0, 12));
    } catch {
      setNewsItems([]);
    } finally {
      setLoadingNews(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 60000);
    return () => clearInterval(interval);
  }, [fetchNews]);

  const homeActions = CCC_HOME_ACTIONS;

  const handleNavigation = (sectionIndex: number) => {
    if (isTransitioning || sectionIndex === currentSection) return;
    setIsTransitioning(true);
    if (mainPageContainerRef.current) {
      mainPageContainerRef.current.style.filter = 'blur(6px)';
      mainPageContainerRef.current.style.transition = 'filter 0.15s cubic-bezier(0.4, 0, 0.2, 1)';
    }
    setTimeout(() => {
      setCurrentSection(sectionIndex);
      setTimeout(() => {
        if (mainPageContainerRef.current) {
          mainPageContainerRef.current.style.filter = 'blur(0px)';
        }
        setTimeout(() => setIsTransitioning(false), 100);
      }, 50);
    }, 150);
  };

  const getNewsCategoryColor = (category: string) => {
    switch (category) {
      case 'success':
        return '#10b981';
      case 'warning':
        return '#f59e0b';
      case 'announcement':
        return '#8b5cf6';
      default:
        return '#e6001a';
    }
  };

  return (
    <div
      className="cc-dashboard-container ccc-dashboard callcenter-dashboard"
      ref={mainPageContainerRef}
    >
      <section
        ref={heroRef}
        className={`cc-section ${currentSection === 0 ? 'section-active' : 'section-hidden'}`}
        style={{ display: currentSection === 0 ? 'block' : 'none' }}
      >
        <div className="cc-hero">
          <div className="cc-hero-content">
            <div className="cc-hero-text">
              <h1 className="cc-hero-title">Página Principal</h1>
              <p className="cc-hero-subtitle">
                CallCenter Comercial — recuperação e operação Comercial
              </p>
            </div>
          </div>
        </div>

        <div className="cc-quick-actions">
          {homeActions.map((action) => (
            <div
              key={action.path}
              className={`cc-action-card ccc-action-${action.variant}`}
              onClick={() => navigate(action.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(action.path);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="cc-action-icon-wrapper">{ACTION_ICONS[action.variant]}</div>
              <div className="cc-action-content">
                <h3 className="cc-action-title">{action.title}</h3>
                <p className="cc-action-desc">{action.description}</p>
              </div>
              <div className="cc-action-arrow">→</div>
            </div>
          ))}
        </div>

        {/* Recuperação KPI cards disabled — stats endpoint is slow on large datasets.
        <div className="cc-stats-grid">
          ...
        </div>
        */}

        <div className="cc-recent-section">
          <div className="cc-section-header">
            <h2 className="cc-section-title">Recuperação — área de trabalho</h2>
            <Button type="link" onClick={() => navigate('/recuperacao')}>
              Abrir Recuperação →
            </Button>
          </div>
          <div className="cc-recent-list">
            {recentMine.length > 0 ? (
              recentMine.map((m) => {
                const id = m.marcacao_id || String(m.id);
                const summary = getMarcacaoRowSummary(m);
                const substage = getWorkspaceSubstage(m);
                return (
                  <div
                    key={id}
                    className="cc-recent-item"
                    onClick={() => navigate('/recuperacao')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate('/recuperacao');
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="cc-recent-icon"
                      style={{
                        background: 'rgba(230, 0, 26, 0.12)',
                        color: '#e6001a',
                      }}
                    >
                      <ReloadOutlined />
                    </div>
                    <div className="cc-recent-content">
                      <div className="cc-recent-title">{summary.empresa}</div>
                      <div className="cc-recent-meta">
                        <Tag color="blue">{summary.produto}</Tag>
                        <Tag>{subsectionLabel(substage)}</Tag>
                      </div>
                    </div>
                    <div className="cc-recent-time">
                      {summary.schedule}
                      <span className="cc-recent-hour">{summary.responsavel}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="cc-empty-state">
                <ReloadOutlined style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 16 }} />
                <p>Ainda não tem marcações na sua área de trabalho</p>
                <Button type="primary" danger onClick={() => navigate('/recuperacao')}>
                  Ir para Recuperação
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section
        ref={newsRef}
        className={`cc-section ${currentSection === 1 ? 'section-active' : 'section-hidden'}`}
        style={{ display: currentSection === 1 ? 'block' : 'none' }}
      >
        <div className="cc-hero">
          <div className="cc-hero-content">
            <div className="cc-hero-text">
              <h1 className="cc-hero-title">Notícias e Atualizações</h1>
              <p className="cc-hero-subtitle">Comunicados partilhados com a operação CallCenter</p>
            </div>
          </div>
        </div>

        <div className="cc-news-board">
          {loadingNews ? (
            <div className="cc-loading-state">A carregar notícias…</div>
          ) : newsItems.length > 0 ? (
            <div className="cc-news-list">
              {newsItems.map((news) => {
                const color = getNewsCategoryColor(news.category);
                return (
                  <article
                    key={news.id}
                    className={`cc-news-item ${news.is_pinned ? 'cc-news-pinned' : ''}`}
                  >
                    {news.is_pinned && <div className="cc-news-pin-badge">Fixado</div>}
                    <div className="cc-news-item-header">
                      <div className="cc-news-item-meta">
                        <div
                          className="cc-news-category-icon"
                          style={{
                            background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
                            border: `2px solid ${color}30`,
                            color,
                          }}
                        >
                          <InfoCircleOutlined />
                        </div>
                        <div className="cc-news-item-info">
                          <h3 className="cc-news-item-title">{news.title}</h3>
                          <div className="cc-news-item-author">
                            <span className="cc-news-author-name">
                              {news.author_name || 'Equipa'}
                            </span>
                            <span className="cc-news-separator">•</span>
                            <span className="cc-news-date">
                              {formatDateTimeLisbonShort(news.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="cc-news-item-content">
                      <div className="cc-news-content-text">
                        {news.content.split('\n').slice(0, 4).map((paragraph, idx) => (
                          <p key={idx}>{paragraph || '\u00A0'}</p>
                        ))}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="cc-empty-state">
              <FileTextOutlined style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 16 }} />
              <p>Nenhuma notícia disponível</p>
              <span className="cc-empty-state-subtitle">
                Os comunicados publicados no CallCenter aparecem aqui quando existirem.
              </span>
            </div>
          )}
        </div>
      </section>

      {createPortal(
        <div className="cc-navigation">
          {sections.map((section, index) => (
            <Button
              key={section.name}
              type={currentSection === index ? 'primary' : 'default'}
              icon={section.icon}
              onClick={() => handleNavigation(index)}
              className={`cc-nav-button ${currentSection === index ? 'cc-nav-active' : ''}`}
              disabled={isTransitioning}
            >
              {section.name}
            </Button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default HomePage;
